import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fab,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';

export interface DocumentAction {
  icon: React.ReactElement;
  label: string;
  onClick: (item: any) => void;
  condition?: (item: any) => boolean;
}

export interface DocumentListPageProps<T> {
  title: string;
  documents: T[];
  columns: GridColDef[];
  loading?: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onCreateNew: () => void;
  onRowClick: (item: T) => void;
  actions: DocumentAction[];
  getRowId?: (row: T) => string;
  statusColorMap?: Record<string, string>;
}

export function DocumentListPage<T extends Record<string, any>>({
  title,
  documents,
  columns,
  loading = false,
  searchTerm,
  onSearchChange,
  onCreateNew,
  onRowClick,
  actions,
  getRowId = (row) => row.id,
  statusColorMap = {}
}: DocumentListPageProps<T>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: T) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const enhancedColumns: GridColDef[] = [
    ...columns,
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreVertIcon />}
          label="More"
          onClick={(event: React.MouseEvent<HTMLElement>) => handleMenuClick(event, params.row)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {title}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          onClick={onCreateNew}
        >
          Create {title.slice(0, -1)}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          rows={documents}
          columns={enhancedColumns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowId={getRowId}
          onRowClick={(params) => onRowClick(params.row)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#0d47a1',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.04)',
              cursor: 'pointer'
            }
          }}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2 }
          }
        }}
      >
        {actions.map((action, index) => {
          const shouldShow = !action.condition || action.condition(selectedItem);
          if (!shouldShow) return null;
          
          return (
            <MenuItem
              key={index}
              onClick={() => {
                handleMenuClose();
                if (selectedItem) {
                  action.onClick(selectedItem);
                }
              }}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Floating Action Button */}
      <Fab
        color="secondary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={onCreateNew}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}